/**
 * Background music: which track belongs to which part of the game, and the
 * crossfade between them.
 *
 * The one place the project ships **real audio files**. Everything else in
 * `audio/` is synthesised, because a synth is a few lines of code and a sound
 * effect is heard ten thousand times a session; a three-minute orchestral track
 * is neither of those things, so the three tracks live in `public/music/` and
 * stream.
 *
 * The decision half of this file — which track, and when to change — is pure and
 * tested. The `HTMLAudioElement` plumbing is not, for the same reason `mixer.ts`
 * is not: it needs a real `AudioContext`.
 */

import { MUSIC_BUS_GAIN } from './mixer';

/** The three tracks, one per state the game can be in. */
export type TrackId = 'title' | 'hub' | 'dungeon';

export interface TrackDef {
  id: TrackId;
  /** Relative to the Vite base, which is `/grind/hud/` in every environment. */
  file: string;
  /** For the credits, and for saying what is playing when one fails to load. */
  title: string;
}

export const TRACKS: Record<TrackId, TrackDef> = {
  title: { id: 'title', file: 'music/title-theme.m4a', title: 'Main Title Theme' },
  hub: { id: 'hub', file: 'music/town-hub.m4a', title: 'Town Hub — Sanctuary' },
  dungeon: { id: 'dungeon', file: 'music/dungeon-grind.m4a', title: 'Dungeon Grind — Dark Fantasy' },
};

/** How long one track takes to give way to another, in seconds. */
export const CROSSFADE_SECONDS = 1.5;

/**
 * Which track belongs to a given moment.
 *
 * Driven by the two facts that describe where the player is: the shell phase and
 * the zone's kind. Deliberately a pure function of both rather than something the
 * zone transition calls, so there is no state to get out of step — if the client
 * thinks it is in town, town music is playing.
 *
 * Returns null before the first ZONE frame: a fraction of a second where the
 * client genuinely does not know where it is, and starting the wrong track then
 * cutting it is worse than a moment of quiet.
 */
export function trackFor(phase: 'title' | 'playing', zoneKind: string | null): TrackId | null {
  if (phase === 'title') return 'title';
  if (zoneKind === 'HUB') return 'hub';
  if (zoneKind === null) return null;
  return 'dungeon';
}

/** What a director needs of a player, so it can be tested without an element. */
export interface MusicSink {
  /** Crossfades to a track, looping it. */
  play(track: TrackDef): void;
  /** Fades out whatever is playing. */
  stop(): void;
  /** Music-bus volume, 0..1, independent of the master. */
  setVolume(volume: number): void;
  dispose(): void;
}

/**
 * Decides when the music changes.
 *
 * Its whole job is *not* restarting a track that is already playing: the store's
 * signals fire on every snapshot, and a naive effect would re-trigger the town
 * theme thirty times a second.
 */
export class MusicDirector {
  private current: TrackId | null = null;

  constructor(private readonly sink: MusicSink) {}

  /** The track currently playing, for tests and diagnostics. */
  get playing(): TrackId | null {
    return this.current;
  }

  update(phase: 'title' | 'playing', zoneKind: string | null): void {
    const next = trackFor(phase, zoneKind);
    if (next === this.current) return;

    this.current = next;
    if (next === null) this.sink.stop();
    else this.sink.play(TRACKS[next]);
  }
}

/**
 * Streams the tracks through the mixer's music bus.
 *
 * Each track gets its own element and gain node, kept for the session: an
 * `HTMLAudioElement` may be attached to a `MediaElementAudioSourceNode` exactly
 * once, so rebuilding one per transition would need a new element every time and
 * re-download the track. `preload="none"` keeps that from costing anything until
 * a track is first wanted.
 */
export function createMusicPlayer(
  context: AudioContext,
  destination: AudioNode,
  volume: number,
  baseUrl: string,
): MusicSink {
  const bus = context.createGain();
  bus.gain.value = clamp01(volume) * MUSIC_BUS_GAIN;
  bus.connect(destination);

  const loaded = new Map<TrackId, { el: HTMLAudioElement; gain: GainNode }>();
  let current: TrackId | null = null;

  const nodeFor = (track: TrackDef) => {
    const existing = loaded.get(track.id);
    if (existing) return existing;

    const el = new Audio(baseUrl + track.file);
    el.loop = true;
    el.preload = 'none';
    // A stream that stalls should not take the game with it.
    el.addEventListener('error', () => {
      console.warn(`grind: could not load "${track.title}"`);
    });

    const gain = context.createGain();
    gain.gain.value = 0;
    context.createMediaElementSource(el).connect(gain).connect(bus);

    const node = { el, gain };
    loaded.set(track.id, node);
    return node;
  };

  const fade = (gain: GainNode, to: number) => {
    const t = context.currentTime;
    gain.gain.cancelScheduledValues(t);
    gain.gain.setValueAtTime(gain.gain.value, t);
    gain.gain.linearRampToValueAtTime(to, t + CROSSFADE_SECONDS);
  };

  return {
    play(track) {
      const next = nodeFor(track);

      for (const [id, node] of loaded) {
        if (id === track.id) continue;
        fade(node.gain, 0);
        // Paused after the fade, not during it, or the crossfade is a cut.
        window.setTimeout(() => {
          if (current !== id) node.el.pause();
        }, CROSSFADE_SECONDS * 1000);
      }

      current = track.id;
      fade(next.gain, 1);
      // `play()` rejects if the gesture requirement is not met. The context only
      // exists after one, so this should not happen — but an unhandled rejection
      // in the console would look like a bug in the game rather than a policy.
      void next.el.play().catch(() => {});
    },

    stop() {
      current = null;
      for (const node of loaded.values()) {
        fade(node.gain, 0);
        window.setTimeout(() => node.el.pause(), CROSSFADE_SECONDS * 1000);
      }
    },

    setVolume(v) {
      const t = context.currentTime;
      bus.gain.cancelScheduledValues(t);
      bus.gain.setTargetAtTime(clamp01(v) * MUSIC_BUS_GAIN, t, 0.05);
    },

    dispose() {
      for (const node of loaded.values()) {
        node.el.pause();
        node.el.src = '';
      }
      loaded.clear();
      bus.disconnect();
    },
  };
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(1, v));
}
